/**
 * Shared UI primitives for super-admin v2 (extracted from mock).
 */
import type { CSSProperties, ReactNode, MouseEvent } from "react";
import { C, FONT } from "../../styles/theme";

export function PageHeader({ title, subtitle, actions }: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      flexWrap: "wrap", gap: 16,
      marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${C.border}`,
    }}>
      <div>
        <div style={{ fontFamily: FONT.display, fontSize: 28, fontWeight: 700, color: C.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{subtitle}</div>}
      </div>
      {actions}
    </div>
  );
}

export function StatusPill({ children, color, bg }: { children: ReactNode; color: string; bg: string }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", background: bg, borderRadius: 100,
      fontSize: 10, fontWeight: 700, color,
    }}>{children}</div>
  );
}

export function KpiCard({ icon, label, value, change, color }: {
  icon: ReactNode;
  label: string;
  value: string | number;
  change?: string;
  color?: string;
}) {
  const accent = color ?? C.brand;
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${accent}20`, color: accent,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
        }}>{icon}</div>
        {change && (
          <div style={{
            fontSize: 11, color: C.green, fontWeight: 700,
            background: C.greenSoft, padding: "2px 8px", borderRadius: 100,
          }}>{change}</div>
        )}
      </div>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: FONT.display, fontSize: 28, fontWeight: 700, color: C.text, marginTop: 6 }}>{value}</div>
    </div>
  );
}

export function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: 11, color: C.muted, letterSpacing: 1,
        textTransform: "uppercase", fontWeight: 700, marginBottom: 6,
      }}>{label}</div>
      {children}
    </div>
  );
}

export function ModalWrapper({ children, onClose, wide }: {
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const handleBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };
  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: C.bgCard, border: `1px solid ${C.border}`,
        borderRadius: 20, maxWidth: wide ? 720 : 540, width: "100%",
        maxHeight: "90vh", overflow: "auto",
        boxShadow: "0 30px 80px rgba(0,0,0,0.8)",
      }}>
        {children}
      </div>
    </div>
  );
}

export function ComingSoon({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: 60, textAlign: "center",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ fontSize: 60, marginBottom: 16, color: C.muted }}>{icon}</div>
      <div style={{ color: C.muted, fontSize: 14, maxWidth: 400, margin: "0 auto" }}>{text}</div>
    </div>
  );
}

export function Avatar({ text, size = 40, gradient = true, shape = "rounded" }: {
  text: string;
  size?: number;
  gradient?: boolean;
  shape?: "rounded" | "circle";
}): JSX.Element {
  const style: CSSProperties = {
    width: size, height: size, borderRadius: shape === "circle" ? "50%" : Math.round(size / 4),
    background: gradient ? `linear-gradient(135deg, ${C.brand}, ${C.brandDark})` : C.brand,
    color: "#0A0E14",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: Math.round(size * 0.4), fontWeight: 800, fontFamily: FONT.display,
    flexShrink: 0,
  };
  return <div style={style}>{text}</div>;
}
